import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, Video, Phone, MapPin, Link2, MessageCircle, ShieldCheck, ChevronRight, ChevronLeft, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import BookReview from "@/components/BookReview";
import { useLang } from "@/context/LanguageContext";
import { detectTZ, COMMON_TZS, slotDate, fmtTime, tzAbbrev, localHour, BUSINESS_TZ } from "@/lib/tz";
import { PushReminders } from "@/components/PushReminders";
import { FamilyPetsFields } from "@/components/FamilyPetsFields";
import { trackAbandon, trackComplete } from "@/lib/abandon";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-4 py-3 text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50 transition-all";
const LABEL = "text-sm font-medium text-charcoal-600 mb-2 block";

export const METHODS = {
  video: { label: "Video call", icon: Video },
  phone: { label: "Phone call", icon: Phone },
  zoom: { label: "Zoom", icon: Video },
  google_meet: { label: "Google Meet", icon: Video },
  teams: { label: "Microsoft Teams", icon: Video },
  facetime: { label: "FaceTime", icon: Video },
  whatsapp_call: { label: "WhatsApp call", icon: MessageCircle },
  whatsapp_video: { label: "WhatsApp video", icon: MessageCircle },
  in_person: { label: "In person", icon: MapPin },
  custom: { label: "Meeting link", icon: Link2 },
};
const PHONE_METHODS = ["phone", "whatsapp_call", "whatsapp_video", "facetime"];

function formatDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function formatTime(t) {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
}

function MonthCalendar({ availableDates, selectedDate, onSelectDate }) {
  const first = availableDates[0];
  const [cursor, setCursor] = useState(() => {
    const base = first ? new Date(first + "T00:00:00") : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  useEffect(() => {
    if (first) {
      const d = new Date(first + "T00:00:00");
      setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [first]);
  const availSet = useMemo(() => new Set(availableDates), [availableDates]);
  const offset = (new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const iso = (day) => `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return (
    <div data-testid="book-calendar">
      <div className="mb-4 flex items-center justify-between">
        <button type="button" data-testid="cal-prev-month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="rounded-full border border-sand-200 p-2 text-charcoal-500 hover:border-terracotta hover:text-terracotta"><ChevronLeft size={16} /></button>
        <p className="font-medium text-charcoal-800">{cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
        <button type="button" data-testid="cal-next-month" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="rounded-full border border-sand-200 p-2 text-charcoal-500 hover:border-terracotta hover:text-terracotta"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
          <span key={d} className="pb-2 text-[11px] font-medium tracking-wide text-charcoal-400">{d}</span>
        ))}
        {cells.map((day, i) => {
          if (!day) return <span key={`x${i}`} />;
          const dIso = iso(day);
          const open = availSet.has(dIso);
          const sel = selectedDate === dIso;
          return (
            <button type="button" key={dIso} data-testid={`cal-day-${dIso}`} disabled={!open}
              onClick={() => onSelectDate(dIso)}
              className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full text-sm transition-colors
                ${sel ? "bg-terracotta text-white font-medium"
                  : open ? "bg-sage-light text-sage-700 font-medium hover:bg-sage hover:text-white"
                    : "text-charcoal-300 cursor-default"}`}>
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Book() {
  const { t, lang } = useLang();
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selected, setSelected] = useState(null);
  const [method, setMethod] = useState("video");
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_move_date: "", notes: "", preferred_language: "en" });
  useEffect(() => { setForm((f) => ({ ...f, preferred_language: lang })); }, [lang]);
  const [profile, setProfile] = useState({ preferred_name: "", party_adults: null, party_kids: null, personalization_notes: "", pets: [] });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [pricingSel, setPricingSel] = useState(null);
  const [tz, setTz] = useState(detectTZ());
  const [showTzPicker, setShowTzPicker] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackAbandon("scheduler_open");
    api.get("/appointment-types").then((res) => {
      setTypes(res.data);
      if (res.data.length) setTypeId(res.data[0].id);
    });
    try {
      const raw = sessionStorage.getItem("hola_pricing_selection");
      if (raw) {
        const sel = JSON.parse(raw);
        setPricingSel(sel);
        const lines = [`Package: ${sel.package_name || sel.package}`];
        if (sel.direction) lines.push(`Direction: ${sel.direction}`);
        if (sel.addons?.length) lines.push(`Add-ons: ${sel.addons.join(", ")}`);
        if (sel.estimate) lines.push(`Estimated: ${sel.estimate}`);
        setForm((p) => ({ ...p, notes: p.notes ? p.notes : lines.join("\n") }));
      }
    } catch { /* ignore malformed storage */ }
  }, []);

  useEffect(() => {
    if (!typeId) return;
    setSelected(null);
    setSelectedDate(null);
    setAnswers({});
    let first = true;
    const fetchSlots = () =>
      api.get(`/appointment-types/${typeId}/slots`).then((res) => {
        setSlots(res.data.slots);
        if (first) {
          first = false;
          const firstDate = res.data.slots[0]?.date;
          if (firstDate) setSelectedDate(firstDate);
        } else {
          setSelected((sel) => {
            if (sel && !res.data.slots.some((s) => s.datetime === sel.datetime)) {
              setError("That time was just booked by someone else — please pick another.");
              return null;
            }
            return sel;
          });
        }
      }).catch(() => { if (first) setSlots([]); });
    fetchSlots();
    const poll = setInterval(fetchSlots, 25000);
    return () => clearInterval(poll);
  }, [typeId]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const activeType = types.find((x) => x.id === typeId);
  const allowedMethods = activeType?.meeting_methods?.length ? activeType.meeting_methods : ["video", "phone", "whatsapp_call", "whatsapp_video", "in_person"];
  const byDate = useMemo(() => slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {}), [slots]);
  const availableDates = useMemo(() => Object.keys(byDate).sort(), [byDate]);
  const daySlots = selectedDate ? byDate[selectedDate] || [] : [];
  const phoneRequired = PHONE_METHODS.includes(method);

  useEffect(() => {
    if (activeType && !allowedMethods.includes(method)) setMethod(allowedMethods[0]);
  }, [typeId, types]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!typeId) return setError("Please choose an appointment type.");
    if (!selected) return setError("Please choose a date and time.");
    if (phoneRequired && !form.phone.trim()) return setError("Please add a phone number for this meeting method.");
    setLoading(true);
    try {
      const { data } = await api.post("/appointments/request", {
        appointment_type_id: typeId,
        ...form,
        slot: selected.datetime,
        meeting_method: method,
        intake_answers: answers,
        pricing_selection: pricingSel || undefined,
        referral_code: localStorage.getItem("hm_ref") || undefined,
        origin_url: window.location.origin,
      });
      trackComplete("scheduler_open");
      if (data.deposit_required && data.url) {
        sessionStorage.removeItem("hola_pricing_selection");
        window.location.href = data.url;
      } else {
        sessionStorage.removeItem("hola_pricing_selection");
        setDone(true);
        window.scrollTo(0, 0);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="book-request-received" className="mx-auto max-w-2xl px-6 py-24 text-center">
        <CheckCircle2 className="mx-auto text-sage" size={56} strokeWidth={1.5} />
        <h1 className="mt-6 font-serif text-3xl font-light text-charcoal-900">{t("book.receivedTitle")}</h1>
        <p className="mt-3 text-charcoal-600 text-lg">
          Thanks — we've got your {activeType?.name?.toLowerCase()} request. We'll review it and email you a
          confirmation with your meeting details shortly.
        </p>
        <div className="mt-6">
          <PushReminders email={form.email} />
        </div>
        <Link to="/" className="mt-8 inline-flex rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover">{t("book.backHome")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-book">
      <Seo title="Book a Consultation — Plan Your Move to Mexico"
        description="Book a consultation with Hola Movers. Choose your service, meeting method and time — video, phone, WhatsApp or in person."
        path="/book" />
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pt-16 lg:pt-24 pb-8">
        <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">{t("book.eyebrow")}</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-charcoal-900 leading-[1.05] max-w-2xl">{t("book.title")}</h1>
        <div className="mt-6 flex flex-wrap gap-6 text-charcoal-600">
          <span className="inline-flex items-center gap-2"><Video size={18} strokeWidth={1.5} /> Video, phone, WhatsApp or in person</span>
          {activeType && <span className="inline-flex items-center gap-2"><Clock size={18} strokeWidth={1.5} /> {activeType.duration_minutes} {t("book.minutes")}</span>}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-24 grid gap-10 lg:grid-cols-5">
        <form onSubmit={submit} className="lg:col-span-3 space-y-10">
          {/* Appointment type */}
          <div className="rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)]">
            <h2 className="text-xl font-medium text-charcoal-800 mb-6">{t("book.step1")}</h2>
            <div className="grid gap-3">
              {types.map((at, i) => (
                <button type="button" key={at.id} data-testid={`appttype-${at.id}`} onClick={() => setTypeId(at.id)}
                  className={`relative text-left rounded-2xl px-5 py-4 border transition-colors ${typeId === at.id ? "bg-sage-light border-sage" : "bg-sand-100 border-sand-200 hover:border-sage"}`}>
                  {i === 0 && (
                    <span className="absolute -top-2.5 left-5 rounded-full bg-terracotta px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-white">{t("book.mostPopular")}</span>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-charcoal-800">{at.name}</span>
                    <span className="text-sm font-medium text-terracotta">{at.deposit_amount > 0 ? `$${at.deposit_amount} ${t("book.deposit")}` : t("book.free")}</span>
                  </div>
                  <p className="text-sm text-charcoal-600 mt-1">{at.description} · {at.duration_minutes} {t("book.minutes")}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Meeting method */}
          <div className="rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)]">
            <h2 className="text-xl font-medium text-charcoal-800 mb-6">How would you like to meet?</h2>
            <div className="flex flex-wrap gap-2" data-testid="book-methods">
              {allowedMethods.filter((m) => METHODS[m]).map((m) => {
                const M = METHODS[m];
                return (
                  <button type="button" key={m} data-testid={`book-method-${m}`} onClick={() => setMethod(m)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium border transition-colors ${method === m ? "bg-terracotta text-white border-terracotta" : "bg-sand-100 text-charcoal-700 border-sand-200 hover:border-terracotta"}`}>
                    <M.icon size={15} /> {M.label}
                  </button>
                );
              })}
            </div>
            {phoneRequired && <p className="mt-3 text-xs text-charcoal-500">We'll need your phone number below so we can reach you.</p>}
            {method === "in_person" && activeType?.location_address && <p className="mt-3 text-xs text-charcoal-500">Location: {activeType.location_address}</p>}
          </div>

          {/* Calendar + time */}
          <div className="rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)]">
            <h2 className="text-xl font-medium text-charcoal-800 mb-6">{t("book.step2")}</h2>
            {availableDates.length === 0 ? (
              <p className="text-charcoal-500">{t("book.noTimes")}</p>
            ) : (
              <div className="grid gap-8 sm:grid-cols-[1.2fr_1fr]">
                <MonthCalendar availableDates={availableDates} selectedDate={selectedDate}
                  onSelectDate={(d) => { setSelectedDate(d); setSelected(null); }} />
                <div data-testid="book-times">
                  <p className="mb-3 text-sm font-medium text-charcoal-700">
                    {selectedDate ? formatDate(selectedDate) : "Pick a date"}
                  </p>
                  <div className="max-h-[320px] space-y-4 overflow-y-auto pr-1">
                    {[["Morning", (h) => h < 12], ["Afternoon", (h) => h >= 12 && h < 17], ["Evening", (h) => h >= 17]].map(([label, match]) => {
                      const group = daySlots.filter((s) => match(localHour(slotDate(s), tz)));
                      if (group.length === 0) return null;
                      return (
                        <div key={label}>
                          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-charcoal-400">{label}</p>
                          <div className="grid grid-cols-2 gap-2">
                            {group.map((s) => (
                              <button type="button" key={s.datetime} data-testid={`slot-${s.datetime}`} onClick={() => setSelected(s)}
                                className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-colors border ${selected?.datetime === s.datetime ? "bg-terracotta text-white border-terracotta" : "bg-sand-100 text-charcoal-700 border-sand-200 hover:border-terracotta"}`}>
                                {fmtTime(slotDate(s), tz)}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {selectedDate && daySlots.length === 0 && <p className="text-sm text-charcoal-400">No times left this day.</p>}
                  </div>
                </div>
              </div>
            )}
            <div className="mt-5 border-t border-sand-100 pt-4">
              <p data-testid="book-tz-note" className="text-xs text-charcoal-500">
                Times shown in <span className="font-medium text-charcoal-700">{tzAbbrev(tz)} — {tz.replace(/_/g, " ")}</span> (your time) ·
                this appointment is with our Mérida, Mexico team.{" "}
                <button type="button" data-testid="book-tz-change" onClick={() => setShowTzPicker((v) => !v)}
                  className="text-terracotta underline underline-offset-2">Not your timezone?</button>
              </p>
              {showTzPicker && (
                <select data-testid="book-tz-select" className={`${FIELD} mt-2 max-w-xs`} value={tz}
                  onChange={(e) => { setTz(e.target.value); setShowTzPicker(false); }}>
                  {[...new Set([detectTZ(), ...COMMON_TZS])].map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
                </select>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] space-y-6">
            <h2 className="text-xl font-medium text-charcoal-800">{t("book.step3")}</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              <div><label className={LABEL}>{t("book.name")} *</label><input data-testid="book-name" required className={FIELD} value={form.name} onChange={set("name")} /></div>
              <div><label className={LABEL}>{t("book.email")} *</label><input data-testid="book-email" type="email" required className={FIELD} value={form.email} onChange={set("email")}
                onBlur={() => form.email.includes("@") && trackAbandon("scheduler_open", { email: form.email, name: form.name })} /></div>
              <div><label className={LABEL}>{t("book.phone")}{phoneRequired ? " *" : ""}</label><input data-testid="book-phone" required={phoneRequired} className={FIELD} value={form.phone} onChange={set("phone")} /></div>
              <div><label className={LABEL}>{t("book.moveDate")}</label><input data-testid="book-move-date" type="date" className={FIELD} value={form.preferred_move_date} onChange={set("preferred_move_date")} /></div>
              <div><label className={LABEL}>Email me in</label>
                <select data-testid="book-language" className={FIELD} value={form.preferred_language} onChange={set("preferred_language")}>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
              {(activeType?.intake_questions || []).map((q, i) => (
                <div key={i} className="sm:col-span-2">
                  <label className={LABEL}>{q.label}{q.required ? " *" : ""}</label>
                  <input data-testid={`book-intake-${i}`} required={!!q.required} className={FIELD}
                    value={answers[q.label] || ""} onChange={(e) => setAnswers((p) => ({ ...p, [q.label]: e.target.value }))} />
                </div>
              ))}
              <div className="sm:col-span-2"><label className={LABEL}>{t("book.notes")}</label><textarea data-testid="book-notes" rows={3} className={FIELD} value={form.notes} onChange={set("notes")} /></div>
              <div className="sm:col-span-2 rounded-2xl border border-sand-200 bg-sand-100/50 p-5">
                <p className="mb-3 text-sm font-medium text-charcoal-700">You &amp; your pets <span className="font-normal text-charcoal-400">(optional — helps us prepare for your call)</span></p>
                <FamilyPetsFields value={profile} onChange={setProfile} compact />
              </div>
            </div>
            {error && <p data-testid="book-error" className="text-sm text-destructive">{error}</p>}
            <button type="submit" data-testid="book-submit" disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-3.5 text-white font-medium hover:bg-terracotta-hover transition-colors disabled:opacity-60">
              {loading ? t("book.submitting") : activeType?.deposit_amount > 0 ? `${t("book.continueDeposit")} · $${activeType.deposit_amount}` : t("book.requestBtn")}
              {!loading && <ChevronRight size={18} />}
            </button>
            <p data-testid="book-trust-note" className="text-sm text-charcoal-500">
              {activeType?.deposit_amount > 0
                ? "If the call doesn't help, we'll refund it — no questions asked."
                : "No credit card required to book. If a paid call ever doesn't help, we'll refund it — no questions asked."}
            </p>
          </div>
        </form>

        {/* Summary sidebar */}
        <aside className="lg:col-span-2 space-y-6">
          {pricingSel && (
            <div data-testid="book-pricing-selection" className="rounded-3xl bg-white p-8 border-2 border-sage">
              <h3 className="font-serif text-xl text-charcoal-800 mb-3">Your pricing selection</h3>
              <p className="text-charcoal-800 font-medium">{pricingSel.package_name}</p>
              {pricingSel.direction && (
                <span className="mt-1 inline-block rounded-full bg-sage/15 px-2.5 py-0.5 text-xs font-medium text-sage-700" data-testid="book-direction-tag">{pricingSel.direction}</span>
              )}
              {pricingSel.addons?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {pricingSel.addons.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-charcoal-600">
                      <Check size={14} className="mt-0.5 shrink-0 text-sage-700" />{a}
                    </li>
                  ))}
                </ul>
              )}
              {pricingSel.estimate && <p className="mt-3 text-sm font-medium text-terracotta">Estimated: {pricingSel.estimate}</p>}
              <p className="mt-3 text-xs text-charcoal-500">We've added this to your booking notes — exact pricing is confirmed during your consultation.</p>
            </div>
          )}
          <div className="rounded-3xl bg-sage-light p-8">
            <h3 className="font-serif text-xl text-charcoal-800 mb-4">{t("book.howItWorks")}</h3>
            <ul className="space-y-3 text-sm text-charcoal-700">
              {["Choose your service and how you'd like to meet", "Pick a time and share a few details", activeType?.deposit_amount > 0 ? "Pay your deposit to hold the time" : "Submit your request", "We confirm and send your meeting details"].map((item, i) => (
                <li key={i} className="flex items-start gap-3"><Check size={16} className="mt-0.5 shrink-0 text-sage-700" />{item}</li>
              ))}
            </ul>
          </div>
          {selected && activeType && (
            <div data-testid="book-summary" className="rounded-3xl bg-white p-8 border border-sand-200">
              <h3 className="font-serif text-xl text-charcoal-800 mb-3">{t("book.selection")}</h3>
              <p className="text-charcoal-800 font-medium">{activeType.name}</p>
              <p className="text-charcoal-700 mt-1">
                {new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }).format(slotDate(selected))} at {fmtTime(slotDate(selected), tz)} <span className="text-xs text-charcoal-400">({tzAbbrev(tz)} your time)</span>
              </p>
              <p className="text-xs text-charcoal-500 mt-0.5">{fmtTime(slotDate(selected), BUSINESS_TZ)} in Mérida, Mexico</p>
              <p className="text-charcoal-600 text-sm mt-1">{METHODS[method]?.label}</p>
              <p className="text-terracotta text-sm mt-2 font-medium">{activeType.deposit_amount > 0 ? `$${activeType.deposit_amount} ${t("book.deposit")}` : t("book.noDeposit")}</p>
            </div>
          )}
          <BookReview />
          <div className="rounded-3xl bg-white p-8 border border-sand-200">
            <div className="flex items-center gap-2 text-charcoal-800 mb-3"><ShieldCheck size={18} strokeWidth={1.5} className="text-sage-700" /><h3 className="font-medium">{t("book.reqTitle")}</h3></div>
            <p className="text-sm text-charcoal-600 leading-relaxed">{t("book.reqBody")}</p>
          </div>
        </aside>
      </section>

      {selected && activeType && (
        <div data-testid="book-sticky-bar" className="fixed inset-x-0 bottom-0 z-40 border-t border-sand-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_20px_rgba(44,42,41,0.08)]">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-charcoal-800">
                {activeType.name} · {new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }).format(slotDate(selected))} at {fmtTime(slotDate(selected), tz)} <span className="text-charcoal-400">({tzAbbrev(tz)})</span>
              </p>
              <p className="text-xs text-charcoal-500">{fmtTime(slotDate(selected), BUSINESS_TZ)} Mérida time · {METHODS[method]?.label}</p>
            </div>
            <button type="button" data-testid="book-sticky-continue"
              onClick={() => document.querySelector('[data-testid="book-name"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
              className="shrink-0 rounded-full bg-terracotta px-5 py-2 text-sm font-medium text-white hover:bg-terracotta-hover">
              Continue → your details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
